import { DropType, EmptySeatDropType, TicketDropType } from './dropTypes';
import {
    DropTargetPayload,
    SeatingTicket,
    TransformedSeatingGroup,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';

interface DropAction {
    type: DropType;
    payload: {
        activeData: DropTargetPayload;
        overData: DropTargetPayload;
        selectedTicketIds?: string[];
    };
}

enum StoreAction {
    SEAT_GUEST = 'seatGuest',
    UNSEAT_GUEST = 'unseatGuest',
    UNSEAT_MULTIPLE = 'unseatMultiple',
    SWAP_GUESTS = 'swapGuests',
    RESHUFFLE_AND_SEAT = 'reshuffleAndSeat',
    SHOW_ERROR = 'showError',
}

interface ActionPayloadMap {
    [StoreAction.SEAT_GUEST]: {
        ticketId: string;
        tableId: string;
        seatNumber: number;
    };
    [StoreAction.UNSEAT_GUEST]: {
        ticketId: string;
    };
    [StoreAction.UNSEAT_MULTIPLE]: {
        ticketIds: string[];
    };
    [StoreAction.SWAP_GUESTS]: {
        ticketId1: string;
        ticketId2: string;
    };
    [StoreAction.RESHUFFLE_AND_SEAT]: {
        ticketIds: string[];
        tableId: string;
        seatNumber: number | null;
    };
    [StoreAction.SHOW_ERROR]: {
        message: string;
    };
}

type ActionInstruction =
    | { action: StoreAction.SEAT_GUEST; payload: ActionPayloadMap[StoreAction.SEAT_GUEST] }
    | { action: StoreAction.UNSEAT_GUEST; payload: ActionPayloadMap[StoreAction.UNSEAT_GUEST] }
    | {
          action: StoreAction.UNSEAT_MULTIPLE;
          payload: ActionPayloadMap[StoreAction.UNSEAT_MULTIPLE];
      }
    | { action: StoreAction.SWAP_GUESTS; payload: ActionPayloadMap[StoreAction.SWAP_GUESTS] }
    | {
          action: StoreAction.RESHUFFLE_AND_SEAT;
          payload: ActionPayloadMap[StoreAction.RESHUFFLE_AND_SEAT];
      }
    | { action: StoreAction.SHOW_ERROR; payload: ActionPayloadMap[StoreAction.SHOW_ERROR] };

interface ReducerState {
    tickets: Record<string, SeatingTicket>;
    seatingGroups: Record<string, TransformedSeatingGroup>;
}

type DropReducer = (state: ReducerState, action: DropAction) => ActionInstruction;

interface SameTableSwapResult {
    type: TicketDropType.SAME_TABLE_SWAP;
    sourceTicketId: string;
    targetTicketId: string;
}

interface TableFullResult {
    type: TicketDropType.TABLE_FULL;
}

interface UnseatedToTableReshuffleResult {
    type: TicketDropType.UNSEATED_TO_TABLE_RESHUFFLE;
    ticketIds: string[];
    tableId: string;
    seatNumber: number | null;
}

interface TableWithRoomReshuffleResult {
    type: TicketDropType.TABLE_WITH_ROOM_RESHUFFLE;
    ticketIds: string[];
    tableId: string;
    seatNumber: number | null;
}

interface InvalidResult {
    type: TicketDropType.INVALID;
}

type TicketDropResult =
    | SameTableSwapResult
    | TableFullResult
    | UnseatedToTableReshuffleResult
    | TableWithRoomReshuffleResult
    | InvalidResult;

interface SingleToEmptyResult {
    type: EmptySeatDropType.SINGLE_TO_EMPTY;
    ticketId: string;
    tableId: string;
    seatNumber: number;
}

interface SingleToOccupiedResult {
    type: EmptySeatDropType.SINGLE_TO_OCCUPIED;
    ticketId: string;
    tableId: string;
    seatNumber: number;
}

interface MultipleWithCapacityResult {
    type: EmptySeatDropType.MULTIPLE_WITH_CAPACITY;
    ticketIds: string[];
    tableId: string;
    seatNumber: number;
}

interface MultipleExceedsCapacityResult {
    type: EmptySeatDropType.MULTIPLE_EXCEEDS_CAPACITY;
    ticketIds: string[];
    tableId: string;
    seatNumber: number;
}

interface EmptySeatInvalidResult {
    type: EmptySeatDropType.INVALID;
}

type EmptySeatDropResult =
    | SingleToEmptyResult
    | SingleToOccupiedResult
    | MultipleWithCapacityResult
    | MultipleExceedsCapacityResult
    | EmptySeatInvalidResult;

export type {
    DropAction,
    DropReducer,
    ActionInstruction,
    ActionPayloadMap,
    ReducerState,
    TicketDropResult,
    SameTableSwapResult,
    TableFullResult,
    UnseatedToTableReshuffleResult,
    TableWithRoomReshuffleResult,
    InvalidResult,
    EmptySeatDropResult,
    SingleToEmptyResult,
    SingleToOccupiedResult,
    MultipleWithCapacityResult,
    MultipleExceedsCapacityResult,
    EmptySeatInvalidResult,
};

export { StoreAction };
